import Minze from 'minze'
import axios from 'redaxios'
import { AbstractSearchCore, AbstractSubscriber, IFacet, MultipleResults, Result } from '.'
import { ConsoleError, ConsoleLog } from '../logging'
import MainModule from '../main'
import { getCustomPartnerId, Routing } from '../routing'
import { formatUnixTimestamp } from '../time'
import { populateObject, replaceTemplate } from '../utils'
import { IFacetFilters } from './IFacetFilters'
import { ISearchConfig } from './ISearchConfig'

// Constants for index names and facet types
const INDEX_NAMES = {
  PLACES_FULL: 'places_full',
  ARTISTS: 'artists',
  EVENTS_SEARCH: 'events_search',
  EXHIBITIONS_SEARCH: 'exhibitions_search',
} as const

const FACET_NAMES = {
  CLIENT_ID: 'clientId',
  TOURIST_REGION: 'touristRegion',
  HAS_PUBLISHED_EVENTS: 'hasPublishedEvents',
} as const

const CONFIG_TEMPLATE = {
  options: {
    hitsPerPage: {
      default: 30,
    },
    aroundRadius: {
      default: undefined,
    },
    aroundLatLng: {
      default: undefined,
    },
    page: {
      default: 0,
    },
    paginationLimitedTo: {
      default: undefined,
    },
    numericFilters: [],
  },
}

interface AlgoliaParams {
  aroundLatLng?: string
  aroundRadius?: number
  hitsPerPage?: number
  page?: number
  filters?: string
  facets?: string
  query?: string
}

interface AlgoliaIndexQuery {
  indexName?: string
  params?: AlgoliaParams
  objectID?: string
}

export class UrlSearchCore extends AbstractSearchCore {
  pageNumber: number = 0
  facets: IFacet[] = []
  objectId: string = ''
  query: string = ''
  dateFilters: number[] = []
  dateAnalyticsValue: string = ''
  modal: string | null | undefined = null
  index: string[] = []
  sorting: string = ''
  facetFilters: IFacetFilters = {}
  locationLabel: string = ''
  configs: { [key: string]: any } = {}
  page: number = 0
  indexesWithGeo: string[] = []
  indexesWithEmptyQuery: string[] | undefined
  private _initializedIndexes: any = {}

  constructor(groupId: string, config?: any) {
    super(groupId, config)

    this.populateConfigs(config || {})
  }

  private populateConfigs(config: any): void {
    let configTemplate = CONFIG_TEMPLATE
    this.index = config.index || []
    this.indexesWithGeo = config.indexesWithGeo || []
    this.indexesWithEmptyQuery = config.indexesWithEmptyQuery
    this.facets = config?.defaultFacets || []
    this.sorting = config?.sorting || ''
    this.query = config?.defaultQuery
    // setting default values for this.configs
    this.configs = {}
    this.populateOptions(config?.configs, configTemplate?.options)
    this.notifySubscribers()
  }

  private populateOptions(options: any, template?: any): any {
    if (template) {
      for (let k in template) {
        let prop: any = (template as any)[k]
        let defaultValue: any = (template as any)[k]?.default ?? null
        this.configs[k] = populateObject(prop ?? {}, options?.[k], this.index, defaultValue)
      }
    } else {
      for (let k in options) {
        let prop: any = (this.configs as any)[k]
        let defaultValue: any = (this.configs as any)[k]?.default ?? null
        this.configs[k] = populateObject(prop, options?.[k], this.index, defaultValue)
      }
    }
  }

  async getResult(): Promise<MultipleResults> {
    const objectId = this.objectId
    this.unsetObjectId()

    let multiResults: MultipleResults = await this.getPaginatedMultiIndexResult(objectId)
    return multiResults
  }

  private currentFacetFilters(indexName: string): string[][] {
    const currentFacetFilters: string[][] = this.facets
      .filter(facet => Array.isArray(facet.options) && facet.options.length > 0)
      .map(facet =>
        facet.options
          .filter(option => option.selected)
          .map(option => `${option.negate ? 'NOT ' : ''}${facet.name}:${option.value}`)
      )
      .filter(selectedOptions => selectedOptions.length > 0)

    return this.filterByIndex(currentFacetFilters, indexName)
  }

  /**
   * Filters facet filters based on the target index name.
   * Different indexes support different types of facets.
   */
  private filterByIndex(facetFilters: string[][], indexName: string): string[][] {
    const indexSpecificFilters = this.getIndexSpecificFilters(facetFilters, indexName)
    const clientIdFilters = this.extractClientIdFilters(facetFilters)

    return this.mergeClientIdFilters(indexSpecificFilters, clientIdFilters)
  }

  /**
   * Extracts clientId filters from the facet filters array.
   */
  private extractClientIdFilters(facetFilters: string[][]): string[][] {
    return facetFilters.filter(
      filter => this.isClientIdFilter(filter) && this.hasValidFilterContent(filter)
    )
  }

  /**
   * Gets filters that are appropriate for the specified index.
   */
  private getIndexSpecificFilters(facetFilters: string[][], indexName: string): string[][] {
    if (this.isPlacesIndex(indexName)) {
      return this.getPlacesIndexFilters(facetFilters)
    }

    if (this.isArtistsIndex(indexName)) {
      return this.getArtistsIndexFilters(facetFilters)
    }

    // For events and exhibitions indexes
    return this.getEventsAndExhibitionsFilters(facetFilters)
  }

  /**
   * Merges clientId filters with index-specific filters, avoiding duplicates.
   */
  private mergeClientIdFilters(indexFilters: string[][], clientIdFilters: string[][]): string[][] {
    if (clientIdFilters.length === 0) {
      return indexFilters
    }

    const hasExistingClientId = indexFilters.some(filter => this.isClientIdFilter(filter))

    return hasExistingClientId ? indexFilters : [...indexFilters, ...clientIdFilters]
  }

  // Helper methods for index type checking
  private isPlacesIndex(indexName: string): boolean {
    return indexName.includes(INDEX_NAMES.PLACES_FULL)
  }

  private isArtistsIndex(indexName: string): boolean {
    return indexName.includes(INDEX_NAMES.ARTISTS)
  }

  // Helper methods for filter type checking
  private isClientIdFilter(filter: string[]): boolean {
    return filter[0]?.includes(FACET_NAMES.CLIENT_ID) ?? false
  }

  private isTouristRegionFilter(filter: string[]): boolean {
    return filter[0]?.includes(FACET_NAMES.TOURIST_REGION) ?? false
  }

  private isHasPublishedEventsFilter(filter: string[]): boolean {
    return filter[0]?.includes(FACET_NAMES.HAS_PUBLISHED_EVENTS) ?? false
  }

  private hasValidFilterContent(filter: string[]): boolean {
    return filter[0]?.length > 1
  }

  // Index-specific filter methods
  private getPlacesIndexFilters(facetFilters: string[][]): string[][] {
    return facetFilters.filter(
      filter => this.isTouristRegionFilter(filter) || this.isHasPublishedEventsFilter(filter)
    )
  }

  private getArtistsIndexFilters(facetFilters: string[][]): string[][] {
    return facetFilters.filter(filter => this.isHasPublishedEventsFilter(filter))
  }

  private getEventsAndExhibitionsFilters(facetFilters: string[][]): string[][] {
    // Exclude hasPublishedEvents for events and exhibitions indexes
    return facetFilters.filter(filter => !this.isHasPublishedEventsFilter(filter))
  }

  private getMultiIndexQueries(): {
    indexName: string
    query: string
    params: { [key: string]: any }
  }[] {
    const queries: {
      indexName: string
      query: string
      params: { [key: string]: any }
    }[] = []
    const indexes = this.query ? this.index : this.indexesWithEmptyQuery || this.index
    indexes.forEach(indexName => {
      let params: any = {}

      for (let k in this.configs) {
        if (this.configs[k][indexName] !== undefined) {
          if (this.configs[k][indexName] === null) continue

          params[k] =
            this.modal && k === 'hitsPerPage'
              ? this.modal === indexName
                ? this.configs[k].default
                : 0
              : this.configs[k][indexName]
        } else if (this.configs[k].default) {
          params[k] = this.configs[k].default
        }
      }

      params.facetFilters = this.currentFacetFilters(indexName)

      if (
        !indexName.includes(INDEX_NAMES.PLACES_FULL) &&
        !indexName.includes(INDEX_NAMES.ARTISTS)
      ) {
        const now = new Date().getTime()
        const nowUnix = Math.floor(now / 1000)
        const roundedNow = formatUnixTimestamp(nowUnix, 10)
        if (indexName.includes(INDEX_NAMES.EVENTS_SEARCH)) {
          params.numericFilters = [`startDate>=${roundedNow}`]
        }

        if (this.dateFilters.length >= 2) {
          const startDate = this.dateFilters[0]
          const endDate = this.dateFilters[1]

          // for caching purposes we round time so we can reuse cache else caching is useless
          const roundedStartDate = formatUnixTimestamp(startDate, 10)
          const roundedEndDate = formatUnixTimestamp(endDate, 10)

          params.numericFilters = [
            `startDate<=${roundedEndDate}`,
            `endDateForFilter>=${roundedStartDate}`,
          ]
        }
      }

      queries.push({
        indexName: indexName,
        query: this.query,
        params: params,
      })
    })

    return queries
  }

  private async getPaginatedMultiIndexResult(id?: string): Promise<MultipleResults> {
    const queries = this.getMultiIndexQueries()
    const multipleResults: MultipleResults = {}
    let algoliaQueries: AlgoliaIndexQuery[] = []
    let data = null

    for (let i = 0; i < queries.length; i++) {
      if (id) {
        algoliaQueries.push({
          indexName: queries[i].indexName,
          objectID: id,
        } as AlgoliaIndexQuery)
      } else {
        let facets = queries[i]?.params?.facetFilters ?? []
        facets.forEach((facet: string[], index: number) => {
          facets[index] = facet.map(filter => {
            const [key, value] = filter.split(':')
            const trimmedKey = key.trim()
            const trimmedValue = value.trim()
            // Escape double quotes in the value
            const escapedValue = trimmedValue.replace(/"/g, '\\"')
            // Always wrap the value in double quotes
            return `${trimmedKey}:"${escapedValue}"`
          })
        })

        let numericFilters = queries[i]?.params?.numericFilters ?? []
        let filters = [...numericFilters, ...facets]
        filters = filters.map(filter => (Array.isArray(filter) ? filter.join(' OR ') : filter))
        filters = filters.length > 1 ? filters.map(filter => `(${filter})`) : filters
        algoliaQueries.push({
          indexName: queries[i].indexName,
          params: {
            ...(queries[i].params ?? {}),
            query: queries[i].query,
            //facets: queries[i].params?.facetFilters?.join(",") ?? "",
            //filters: queries[i].params?.numericFilters?.join(" AND ") ?? "",
            facets: undefined,
            filters: filters.join(' AND '),
            facetFilters: undefined,
            numericFilters: undefined,
          } as AlgoliaParams,
        } as AlgoliaIndexQuery)
      }
    }
    if (id) {
      data = await this.fetchObjects(algoliaQueries)

      if (data.results && data.results.length === algoliaQueries.length) {
        for (let i = 0; i < algoliaQueries.length; i++) {
          let idx: string = algoliaQueries[i].indexName as string
          multipleResults[idx] = multipleResults[idx] ?? {
            page: 0,
            items: [],
            numberOfPages: 1,
            total: 0,
            itemsPerPage: 0,
          }

          multipleResults[idx].items.push(data.results[i])
          multipleResults[idx].total = multipleResults[idx].total + 1
          multipleResults[idx].itemsPerPage = multipleResults[idx].total + 1
        }
      }
    } else {
      data = await this.fetchQueries(algoliaQueries)

      if (data.results) {
        let dRes = data.results

        for (let r of dRes) {
          multipleResults[r.index] = {
            page: r.page,
            items: r.hits,
            numberOfPages: r.nbPages,
            total: r.nbHits,
            itemsPerPage: r.hitsPerPage,
          }
        }
      }
    }

    return multipleResults
  }

  private async fetchQueries(queries: AlgoliaIndexQuery[]): Promise<any> {
    return await this.fetchResultsAndObjects('queries', queries)
  }

  private async fetchObjects(objects: AlgoliaIndexQuery[]): Promise<any> {
    return await this.fetchResultsAndObjects('objects', objects)
  }

  private algoliaParamsToString(params: AlgoliaParams): string {
    let urlParams = new URLSearchParams()
    let prms: any = params as any

    for (let k in prms) {
      if (!prms[k]) continue

      urlParams.append(k, prms[k])
    }

    return urlParams.toString()
  }

  public async fetchResultsAndObjects(
    fetchType: string,
    queries: AlgoliaIndexQuery[]
  ): Promise<any> {
    const algoliaAppId = this.config.appId
    const algoliaApiKey = import.meta.env.VITE_ALGOLIA_API_KEY || ''
    const url = replaceTemplate(this.config.appUrl, {
      appId: algoliaAppId,
      apiKey: algoliaApiKey,
      fetchType,
    })
    const config = {
      headers: {
        'X-Algolia-API-Key': algoliaApiKey,
        'X-Algolia-Application-Id': algoliaAppId,
        'Content-Type': 'application/json',
      },
    }

    let data: any = {
      requests: [],
    }

    for (let q of queries) {
      if (q.objectID) {
        data.requests.push({
          indexName: q.indexName,
          objectID: q.objectID,
        })
      } else {
        let stringParams = this.algoliaParamsToString(q.params ?? {})

        data.requests.push({
          indexName: q.indexName,
          params: stringParams,
        })
      }
    }

    if (data?.requests?.[0]?.indexName !== 'cities') {
      ConsoleLog(true, 'search core url request', url, data, config, queries)
    }

    try {
      let response = await axios.post(url, data, config)
      return response.data
    } catch (e) {
      ConsoleError(true, e)
    }
    return { results: [] }
  }
  public async goToSearch(redirectToSearch = true, resetPagination = false): Promise<void> {
    if (resetPagination) {
      const indexes = Object.keys(this.configs.page)
      indexes.forEach(index => {
        this.configs.page[index] = 0
      })
    }
    const mainModule = MainModule.getInstance()
    const routing = mainModule.getRouting()
    const lang = mainModule.getLang()
    const searchConfig = { ...this.searchConfig }

    const regionValue = searchConfig.facets?.find(
      facet => facet.name === FACET_NAMES.TOURIST_REGION
    )?.options?.[0]?.value
    const cityValue = searchConfig.locationLabel
    const audienceValues = searchConfig.facets?.find(facet => facet.name === 'audience')?.options
    const disciplineValues = searchConfig.facets?.find(
      facet => facet.name === 'uiAdditionalTypesCode'
    )?.options

    const searchAnalyticsData: {
      search_term?: string
      filtre_localite?: string
      filtre_date?: string
      filtre_disciplines?: string
      filtre_audiences?: string
    } = {
      search_term: searchConfig.query,
    }

    if (regionValue || cityValue) {
      searchAnalyticsData.filtre_localite = regionValue
        ? `region:${regionValue}`
        : cityValue
          ? `ville:${cityValue}`
          : undefined
    }
    if (searchConfig.dateAnalyticsValue) {
      searchAnalyticsData.filtre_date = searchConfig.dateAnalyticsValue
    }
    const disciplines =
      disciplineValues?.filter(discipline => discipline.available && discipline.available) ||
      undefined
    if (disciplines && disciplines.length) {
      searchAnalyticsData.filtre_disciplines = disciplines.map(audience => audience.value).join(',')
    }
    const audiences =
      audienceValues?.filter(audience => audience.available && audience.available) || undefined
    if (audiences && audiences.length) {
      searchAnalyticsData.filtre_audiences = audiences?.map(audience => audience.value).join(',')
    }

    await mainModule.analyticsTrack('search', {
      event: 'search',
      ...searchAnalyticsData,
    })
    // First set the default location
    let location = `${Routing.ROUTES[lang].searchResults}?query=${encodeURIComponent(JSON.stringify(searchConfig))}`

    // Use the customPartnerId directly from the searchConfig
    if (searchConfig.customPartnerId) {
      location = `${Routing.ROUTES[lang].searchResults}?customPartnerId=${searchConfig.customPartnerId}&query=${encodeURIComponent(JSON.stringify(searchConfig))}`
    }

    if (redirectToSearch) {
      routing.goTo(location)
    } else {
      window.history.replaceState({}, '', location)
      await this.executeSearch(searchConfig)
    }
  }

  public async applySearchConfig(
    searchConfig: ISearchConfig,
    notify: boolean = true
  ): Promise<void> {
    this.objectId = searchConfig?.objectId || ''
    this.query = searchConfig?.query === '' ? '' : searchConfig?.query || this.query || ''
    this.facets = searchConfig?.facets || this.facets || []
    this.modal = searchConfig.modal === null ? null : searchConfig.modal || this.modal || null
    this.sorting = searchConfig.sorting || this.sorting || ''
    this.page = searchConfig.page !== undefined ? searchConfig.page : this.page || 0

    this.dateFilters = searchConfig.dateFilters || this.dateFilters || []
    this.dateFilters = this.dateFilters.map(date => formatUnixTimestamp(date, 13))

    this.locationLabel = searchConfig.locationLabel || ''
    this.dateAnalyticsValue = searchConfig.dateAnalyticsValue || ''

    if (searchConfig.hitsPerPage)
      this.configs = { ...this.configs, hitsPerPage: searchConfig.hitsPerPage }
    if (searchConfig.index) this.index = searchConfig.index || []
    if (searchConfig?.useDefaultOptions) {
      this.populateOptions(this.initialConfig?.configs, CONFIG_TEMPLATE?.options)
    } else {
      this.populateOptions(searchConfig?.options || {})
    }

    // Add logic to apply "hasPublishedEvents" facet by default for specific indexes
    if (this.index.includes(INDEX_NAMES.PLACES_FULL) || this.index.includes(INDEX_NAMES.ARTISTS)) {
      const hasPublishedEventsFacet = this.facets.find(
        facet => facet.name === FACET_NAMES.HAS_PUBLISHED_EVENTS
      )
      if (!hasPublishedEventsFacet) {
        this.facets.push({
          name: FACET_NAMES.HAS_PUBLISHED_EVENTS,
          options: [
            {
              value: 'true',
              selected: true,
              available: true,
            },
          ],
        })
      } else {
        const option = hasPublishedEventsFacet.options.find(option => option.value === 'true')
        if (option) {
          option.selected = true
        } else {
          hasPublishedEventsFacet.options.push({
            value: 'true',
            selected: true,
            available: true,
          })
        }
      }
    }

    // Handle customPartnerId in a more structured way
    // First check if searchConfig has customPartnerId
    let partnerId = searchConfig.customPartnerId

    // If not, check if there's one in the URL
    if (!partnerId) {
      const urlPartnerId = getCustomPartnerId()
      if (urlPartnerId !== null) {
        partnerId = urlPartnerId
      }
    }

    // If we have a partnerId from either source, ensure it's in the facets
    if (partnerId) {
      // Update the searchConfig.customPartnerId to ensure it's consistent
      searchConfig.customPartnerId = partnerId

      // Update the facets to include the clientId
      if (!this.facets.find(f => f.name === FACET_NAMES.CLIENT_ID)) {
        this.facets.push({
          name: FACET_NAMES.CLIENT_ID,
          options: [
            {
              value: partnerId,
              selected: true,
              available: true,
            },
          ],
        })
      } else {
        // Update existing clientId facet if needed
        const clientIdFacet = this.facets.find(f => f.name === FACET_NAMES.CLIENT_ID)
        if (
          clientIdFacet &&
          (!clientIdFacet.options[0] || clientIdFacet.options[0].value !== partnerId)
        ) {
          clientIdFacet.options = [
            {
              value: partnerId,
              selected: true,
              available: true,
            },
          ]
        }
      }
    }

    if (notify) await this.notifySubscribers()
  }

  private unsetObjectId(): void {
    this.objectId = ''
  }

  public async executeSearch(
    searchConfig: ISearchConfig,
    disableNotification?: boolean
  ): Promise<MultipleResults> {
    await this.applySearchConfig(searchConfig, !disableNotification)
    let result: MultipleResults = await this.getResult()
    if (!disableNotification) await this.notifySubscribers(result)
    this.unsetObjectId()
    return result
  }

  private async notifySubscribers(result?: MultipleResults): Promise<void> {
    let facets = this.facets
    let query = this.query
    let config = this.searchConfig
    for (let i = 0; i < this.subscribers.length; i++) {
      await this.subscribers[i].onSearchConfigUpdate(config)
      await this.subscribers[i].onFacetUpdate(facets)
      await this.subscribers[i].onSearchTextUpdate(query)
      if (result) await this.subscribers[i].onResultUpdate(result)
    }
  }

  public async subscribe(subscriber: AbstractSubscriber): Promise<void> {
    super.subscribe(subscriber)
    subscriber.onSearchConfigUpdate(this.searchConfig)
    subscriber.onFacetUpdate(this.facets)
    subscriber.onSearchTextUpdate(this.query)
  }

  private handleFacet = (facet: IFacet) => {
    const currentFacet = this.facets.find((value: IFacet) => {
      return value.name === facet.name
    })

    if (!currentFacet) {
      this.facets.push(facet)
    } else {
      const currentFacetOptions = currentFacet.options
      const optionIndex = currentFacetOptions.findIndex(
        option => option.value === facet.options[0].value
      )
      if (optionIndex == -1) {
        currentFacet.options.push(facet.options[0])
      } else {
        const isOptionSelected = currentFacetOptions[optionIndex]?.selected
        currentFacet.options[optionIndex] = {
          ...currentFacetOptions[optionIndex],
          selected: !isOptionSelected,
        }
      }
    }
  }

  public applyMultipleFacets = (facets: IFacet[]) => {
    const currentFacetIndex = this.facets.findIndex((value: IFacet) => {
      return value.name === facets[0]?.name
    })
    if (currentFacetIndex !== -1) this.facets.splice(currentFacetIndex, 1)
    facets.forEach(facet => this.handleFacet(facet))

    this.goToSearch()
  }

  public applyDateFilters = (
    dateFilters: number[],
    noRedirect: boolean = true,
    analyticsValue?: string
  ) => {
    this.dateAnalyticsValue = analyticsValue || ''
    this.dateFilters = dateFilters.map(date => formatUnixTimestamp(date, 13))

    const searchConfig = { ...this.searchConfig }
    this.applySearchConfig(searchConfig, true)

    if (noRedirect) this.goToSearch()
  }

  public clearFacet(facetName: string) {
    const facetIndex = this.facets.findIndex(facet => facet.name === facetName)
    if (facetIndex !== -1) this.facets.splice(facetIndex, 1)
    this.goToSearch()
  }

  public async setFacet(facet: IFacet): Promise<MultipleResults> {
    this.handleFacet(facet)
    this.notifySubscribers()
    this.page = 0
    let result: Result | MultipleResults = await this.getResult()
    this.notifySubscribers(result)
    return result
  }

  public getFacets(): IFacet[] {
    return this.facets
  }

  public async getObjects(index: string, ids: string[]): Promise<any[]> {
    if (ids.length <= 0) {
      return []
    }

    let objectsToRequest: AlgoliaIndexQuery[] = []

    ids.map(k => {
      objectsToRequest.push({
        indexName: index,
        objectID: k,
      })
    })

    const objects = await this.fetchObjects(objectsToRequest)
    return objects.results
  }

  public async setPage(
    pageNumber: number,
    indexName?: string,
    notify?: boolean
  ): Promise<MultipleResults> {
    this.page = pageNumber

    if (!this.configs?.page) {
      this.configs.page = {
        default: 0,
      }
    }

    if (indexName) {
      this.configs.page[indexName] = pageNumber
    } else {
      this.configs.page.default = pageNumber
    }

    let result: Result | MultipleResults = await this.getResult()
    if (notify) await this.notifySubscribers(result)
    return result
  }

  public destroy(): void {
    super.destroy()
  }

  public async clearAll(redirectToSearch = true): Promise<void> {
    // Clear facets directly to avoid automatic re-addition in applySearchConfig
    this.facets = []
    this.query = ''
    this.page = 0
    this.modal = null
    this.sorting = 'default'
    this.dateFilters = []
    this.dateAnalyticsValue = ''
    this.locationLabel = ''

    // Reset configs to defaults
    if (this.initialConfig?.configs) {
      this.populateOptions(this.initialConfig.configs, CONFIG_TEMPLATE?.options)
    }

    await this.notifySubscribers()
    Minze.cast('lvc:clear-search')
    this.goToSearch(redirectToSearch)
  }
  public async resetAllConfigs(): Promise<void> {
    await this.applySearchConfig(
      {
        facets: [],
        query: '',
        page: 0,
        modal: null,
        sorting: 'default',
        dateFilters: [],
        useDefaultOptions: true,
      },
      false
    )
  }
  public get searchConfig(): ISearchConfig {
    // Get the customPartnerId from the clientId facet if it exists
    const clientIdFacet = this.facets.find(facet => facet.name === FACET_NAMES.CLIENT_ID)
    const customPartnerId = clientIdFacet?.options?.[0]?.value

    return {
      facets: this.facets,
      query: this.query,
      page: this.page,
      modal: this.modal,
      sorting: this.sorting,
      dateFilters: this.dateFilters,
      dateAnalyticsValue: this.dateAnalyticsValue,
      locationLabel: this.locationLabel,
      options: this.configs,
      customPartnerId, // Add the customPartnerId to the search config
    }
  }

  public getIndexesWithGeo(): string[] {
    return this.indexesWithGeo
  }

  public serialize(): any {
    let serialized: any = {}

    serialized.pageNumber = this.page
    serialized.facets = this.facets
    serialized.query = this.query
    serialized.sorting = this.sorting
    serialized.modal = this.modal
    serialized.facetFilters = this.facetFilters
    serialized.dateFilters = this.dateFilters
    serialized.configs = this.configs

    return serialized
  }

  public deserialize(data: any): UrlSearchCore {
    this.pageNumber = data.page
    this.facets = data.facets
    this.query = data.query
    this.sorting = data.sorting
    this.modal = data.modal
    this.facetFilters = data.facetFilters
    this.dateFilters = data.dateFilters
    this.configs = data.configs

    return this
  }
}
